// Billing & expenses (Blueprint §6, R-023) — the pass-through cost ledger and
// the sponsor invoice-out.
//
// Ported from legacy loadBilling / renderBilling / openAddExpense /
// openDispatchEvidence / deleteExpense / exportBillingReport, onto the 2.0 data
// layer: every total and grouping comes from src/data/billing.js (pure, tested),
// this file does layout and interaction only.
//
// Two things this screen must never do, both learned from the legacy version:
//  - never render a figure from a failed load. The expense list is all-or-
//    nothing like Reports: a partial ledger looks exactly like a complete one,
//    and here the number ends up on an invoice.
//  - never hard-delete. The ✕ voids the row (deleted_at) and writes an audit
//    line; the money stays in the table for the KRA trail.
//
// R-023, the sponsor statement, is a PREVIEW on this page before it is an
// export: the finance officer sees the exact line items, totals and stamp that
// will land in the sponsor's inbox, and can print the panel directly.

import { store, SLICES } from '../state/store.js';
import { esc } from '../ui/esc.js';
import { toast } from '../ui/toast.js';
import { register } from '../ui/actions.js';
import { openModal, closeModal, setBtnBusy } from '../ui/modal.js';
import { netErr } from '../errors.js';
import { registerView } from './registry.js';
import { allCases, loadAllCases } from '../data/cases.js';
import { pillLabel } from '../programmes.js';
import { isAdmin } from '../auth.js';
import {
  EXPENSE_CATEGORIES,
  PAYABLE_CATEGORIES,
  PAYMENT_METHODS,
  categoryLabel,
  methodLabel,
  monthPreset,
  isoDay,
  filterByCategory,
  expenseTotals,
  groupByCategory,
  parseEvidence,
  hasReceipt,
  amountOf,
  buildExpenseRow,
  validateExpense,
  buildStatement,
  buildStatementSheets,
  statementFileName,
  writeStatementWorkbook,
  loadExpenses,
  uploadReceipts,
  insertExpense,
  softDeleteExpense,
  signedReceiptUrl,
} from '../data/billing.js';

// ─── Module state ──────────────────────────────────────────────────────────

/** @type {{from: string, to: string, category: string}} */
let _range = { from: '', to: '', category: 'all' };
/** @type {any[]} */
let _rows = [];
let _loaded = false;
/** @type {any} */
let _error = null;
let _busy = false;
let _wired = false;
/** Statement panel: open state, programme scope and billable-only toggle. */
let _stmt = { open: false, programme: 'all', billableOnly: true };

/** @returns {any} */
function doc() {
  return /** @type {any} */ (globalThis).document || null;
}

/** @returns {boolean} */
function mounted() {
  const d = doc();
  return !!(d && d.getElementById('bill-root'));
}

/** Money for the screen: KES with thousands separators, never NaN. @param {number} n @returns {string} */
function money(n) {
  const v = isFinite(Number(n)) ? Number(n) : 0;
  return 'KES ' + v.toLocaleString('en-KE', { maximumFractionDigits: 2 });
}

/** The case catalogue for the patient pickers (shared PATIENTS slice). @returns {any[]} */
function caseOptions() {
  return allCases()
    .slice()
    .sort((a, b) => String(a.patient_name || '').localeCompare(String(b.patient_name || '')));
}

/** @param {string} caseId @returns {any} */
function caseById(caseId) {
  return allCases().find((c) => c && c.case_id === caseId) || null;
}

// ─── Load ──────────────────────────────────────────────────────────────────

async function refresh() {
  if (!_range.from || !_range.to) {
    const p = monthPreset('this');
    _range.from = p.from;
    _range.to = p.to;
  }
  _busy = true;
  render();
  // Warm the case catalogue alongside the ledger: the Add-expense and
  // Dispatch-evidence patient pickers read the shared PATIENTS slice, and a
  // nurse who lands DIRECTLY on Billing (bookmark, refresh) would otherwise
  // get an empty picker until she happened to visit another view first.
  // Best-effort — a failed catalogue read must not cost her the ledger.
  const [res] = await Promise.all([
    loadExpenses({ from: _range.from, to: _range.to }),
    loadAllCases().catch(() => null),
  ]);
  _busy = false;
  _loaded = true;
  if (!res.ok) {
    // ALL-OR-NOTHING: drop the stale rows too. Showing last query's ledger under
    // this query's date header is how the wrong month gets invoiced.
    _rows = [];
    _error = res.error;
    toast(netErr(res.error, 'Could not load expenses'), 'err');
  } else {
    _rows = res.data || [];
    _error = null;
  }
  if (mounted()) render();
  // See reports.js: returned so Sync cannot report success over a failed load,
  // and flagged as already-reported because the netErr toast above fired.
  return _error ? { ok: false, error: _error, code: 'LOAD_FAILED', reported: true } : res;
}

// ─── Render ────────────────────────────────────────────────────────────────

function skeleton() {
  return (
    '<div id="billing-view" class="bill">' +
    '<header class="rpt-head">' +
    '<div><h1 class="rpt-h">Billing &amp; <span>Expenses</span></h1>' +
    '<p class="rpt-meta">Pass-through costs, receipts and dispatch evidence</p></div>' +
    '<div class="rpt-head-a">' +
    '<button type="button" class="btn" data-action="bill-add">+ Add expense</button>' +
    '<button type="button" class="btn" data-action="bill-dispatch">Dispatch evidence</button>' +
    '<button type="button" class="btn pri" data-action="bill-stmt">Sponsor statement</button>' +
    '</div></header>' +
    '<div id="bill-root"><div class="rpt-skel"></div><div class="rpt-skel"></div></div>' +
    '</div>'
  );
}

function filterBarHTML() {
  const cats = ['<option value="all">All categories</option>']
    .concat(
      EXPENSE_CATEGORIES.map(
        (c) => `<option value="${esc(c.value)}"${_range.category === c.value ? ' selected' : ''}>${esc(c.label)}</option>`
      )
    )
    .join('');
  return (
    '<div class="bill-bar">' +
    '<div class="rpt-seg bill-seg">' +
    '<button type="button" class="rpt-seg-b" data-action="bill-preset" data-preset="this">This month</button>' +
    '<button type="button" class="rpt-seg-b" data-action="bill-preset" data-preset="last">Last month</button>' +
    '</div>' +
    `<label class="bill-f"><span>From</span><input type="date" id="bill-from" value="${esc(_range.from)}"/></label>` +
    `<label class="bill-f"><span>To</span><input type="date" id="bill-to" value="${esc(_range.to)}"/></label>` +
    `<label class="bill-f"><span>Category</span><select id="bill-cat">${cats}</select></label>` +
    '<span class="bill-spacer"></span>' +
    '<button type="button" class="btn sm" data-action="bill-reload">Reload</button>' +
    '</div>'
  );
}

/** @param {any} t @returns {string} */
function kpisHTML(t) {
  const tiles = [
    { l: 'Total in period', v: money(t.total), s: t.count + (t.count === 1 ? ' transaction' : ' transactions') },
    { l: 'Billable to sponsor', v: money(t.billable), s: t.total ? Math.round((t.billable / t.total) * 100) + '% of spend' : 'nothing recorded' },
    { l: 'Not billable', v: money(t.nonBillable), s: 'absorbed by the programme' },
    { l: 'Patients with costs', v: String(t.patients), s: 'programme-level costs excluded' },
  ];
  return (
    '<section class="rpt-kpis bill-kpis" aria-label="Expense totals">' +
    tiles
      .map(
        (k) =>
          '<div class="rpt-kpi"><div class="rpt-kpi-l">' +
          esc(k.l) +
          `</div><div class="rpt-kpi-v">${esc(k.v)}</div><div class="rpt-kpi-d">${esc(k.s)}</div></div>`
      )
      .join('') +
    '</section>'
  );
}

/** @param {any[]} rows @returns {string} */
function byCategoryHTML(rows) {
  const groups = groupByCategory(rows);
  if (!groups.length) return '<div class="bill-empty">No expenses in this period.</div>';
  const max = Math.max(1, ...groups.map((g) => g.total));
  return (
    '<div class="bill-cats">' +
    groups
      .map(
        (g) =>
          '<div class="bill-cat"><span class="bill-cat-l">' +
          esc(g.label) +
          // data-w, not an inline style: the deployed CSP is `style-src 'self'`,
          // which blocks style ATTRIBUTES too. See applyBarWidths below.
          `</span><span class="bill-cat-bar"><i data-w="${((g.total / max) * 100).toFixed(1)}"></i></span>` +
          `<span class="bill-cat-n">${esc(String(g.count))}</span>` +
          `<span class="bill-cat-v">${esc(money(g.total))}</span></div>`
      )
      .join('') +
    '</div>'
  );
}

/** @param {any[]} rows @returns {string} */
function listHTML(rows) {
  if (!rows.length) {
    return '<div class="bill-empty">No expenses found for this period and category.</div>';
  }
  const head =
    '<div class="bill-row bill-head"><span>Date</span><span>Description</span><span>Patient / paid to</span>' +
    '<span>Category</span><span class="ta-r">Amount</span><span class="ta-r">Billable</span><span></span></div>';
  return (
    '<div class="bill-list">' +
    head +
    rows
      .map((e) => {
        const isEvidence = e.category === 'dispatch_evidence';
        return (
          `<div class="bill-row"><span class="mono">${esc(e.expense_date || '—')}</span>` +
          `<span class="bill-desc"><button type="button" class="bill-link" data-action="bill-detail" data-id="${esc(e.expense_id)}">` +
          esc(e.description || categoryLabel(e.category)) +
          '</button>' +
          (hasReceipt(e) ? '<span class="bill-clip" title="Receipt attached" aria-label="Receipt attached">&#128206;</span>' : '') +
          '</span>' +
          `<span class="bill-who">${esc(e.patient_name || e.paid_to || '—')}</span>` +
          `<span><span class="bill-tag">${esc(categoryLabel(e.category))}</span></span>` +
          `<span class="ta-r mono">${esc(isEvidence ? '—' : money(amountOf(e.amount)))}</span>` +
          `<span class="ta-r">${e.is_billable ? '<span class="bill-tag ok">Billable</span>' : '<span class="bill-tag">No</span>'}</span>` +
          `<span class="ta-r"><button type="button" class="btn sm ghost bill-x" data-action="bill-delete" data-id="${esc(e.expense_id)}" ` +
          `aria-label="Void this expense">&#10005;</button></span></div>`
        );
      })
      .join('') +
    '</div>'
  );
}

/**
 * The R-023 sponsor statement preview — what the exported workbook will contain,
 * shown before it is sent. `bill-print` prints this panel alone.
 * @param {any[]} periodRows
 * @returns {string}
 */
function statementHTML(periodRows) {
  const prog = store.get(SLICES.PROGRAMMES) || {};
  const byCode = prog.byCode || {};
  const list = Object.keys(byCode).map((k) => byCode[k]);
  const scoped =
    _stmt.programme === 'all'
      ? periodRows
      : periodRows.filter((e) => String(e.programme_id) === String(_stmt.programme));
  const progLabel =
    _stmt.programme === 'all'
      ? pillLabel('all', byCode)
      : (list.find((p) => String(p.programme_id) === String(_stmt.programme)) || {}).display_name || 'Programme';
  const stmt = buildStatement(scoped, {
    programmeLabel: progLabel,
    from: _range.from,
    to: _range.to,
    generatedAt: new Date().toISOString().slice(0, 16).replace('T', ' '),
    billableOnly: _stmt.billableOnly,
  });

  // Built from the real builder, so the preview cannot drift from the file.
  const sheetNames = buildStatementSheets(stmt, scoped).map((s) => s.name);

  const opts = ['<option value="all">All programmes</option>']
    .concat(
      list.map(
        (p) =>
          `<option value="${esc(String(p.programme_id))}"${String(_stmt.programme) === String(p.programme_id) ? ' selected' : ''}>` +
          `${esc(p.display_name || p.code || '')}</option>`
      )
    )
    .join('');

  const lines = stmt.lines.length
    ? stmt.lines
        .map(
          (l) =>
            `<tr><td>${esc(l.label)}</td><td class="ta-r mono">${esc(String(l.count))}</td>` +
            `<td class="ta-r mono">${esc(money(l.total))}</td></tr>`
        )
        .join('')
    : '<tr><td colspan="3" class="bill-empty-cell">Nothing billable in this period and scope.</td></tr>';

  return (
    '<section class="bill-stmt" id="bill-stmt" aria-label="Sponsor statement">' +
    '<div class="bill-stmt-bar no-print">' +
    `<label class="bill-f"><span>Programme</span><select id="bill-stmt-prog">${opts}</select></label>` +
    `<label class="bill-chk"><input type="checkbox" id="bill-stmt-billable"${_stmt.billableOnly ? ' checked' : ''}/>` +
    '<span>Billable items only</span></label>' +
    '<span class="bill-spacer"></span>' +
    '<button type="button" class="btn sm" data-action="bill-print">Print</button>' +
    '<button type="button" class="btn sm pri" data-action="bill-stmt-xlsx">Export .xlsx</button>' +
    // Separate button, separate label, admin-only. The named ledger is never the
    // sponsor artefact, and the two are never one click apart by accident.
    (isAdmin((store.get(SLICES.SESSION) || {}).role)
      ? '<button type="button" class="btn sm" data-action="bill-stmt-internal" ' +
        'title="Adds per-patient line items. Contains identifiers — internal use only.">Internal copy…</button>'
      : '') +
    '<button type="button" class="btn sm ghost" data-action="bill-stmt">Close</button>' +
    '</div>' +
    '<div class="bill-stmt-doc">' +
    '<h3>Tumaini Haven Patient Support Programme</h3>' +
    '<p class="bill-stmt-sub">Sponsor statement — pass-through costs</p>' +
    '<dl class="bill-stmt-meta">' +
    `<div><dt>Programme</dt><dd>${esc(stmt.programmeLabel)}</dd></div>` +
    `<div><dt>Period</dt><dd>${esc(stmt.from)} to ${esc(stmt.to)}</dd></div>` +
    `<div><dt>Generated</dt><dd>${esc(stmt.generatedAt)}</dd></div>` +
    `<div><dt>Basis</dt><dd>${esc(stmt.billableOnly ? 'Billable items only' : 'All recorded items')}</dd></div>` +
    '</dl>' +
    '<table class="bill-stmt-t"><thead><tr><th scope="col">Category</th><th scope="col" class="ta-r">Items</th><th scope="col" class="ta-r">Total (KES)</th></tr></thead>' +
    `<tbody>${lines}</tbody>` +
    `<tfoot><tr><th scope="row">Total billed</th><td class="ta-r mono">${esc(String(stmt.count))}</td>` +
    `<td class="ta-r mono">${esc(money(stmt.grandTotal))}</td></tr></tfoot></table>` +
    (stmt.billableOnly && stmt.excludedTotal
      ? `<p class="bill-stmt-note">${esc(money(stmt.excludedTotal))} of recorded spend in this period is marked non-billable and is ` +
        `not included above. Total recorded spend: ${esc(money(stmt.spentTotal))}.</p>`
      : '') +
    `<p class="bill-stmt-note">Covers ${esc(String(stmt.patients))} patient${stmt.patients === 1 ? '' : 's'} with attributable costs. ` +
    'Amounts are pass-through programme costs only — no staff time and no overheads.</p>' +
    // The preview must describe the SHEETS THAT WILL BE WRITTEN, not a subset —
    // an operator approving an export cannot consent to content the screen hides.
    `<p class="bill-stmt-note no-print"><b>The exported workbook contains ${esc(String(sheetNames.length))} sheet` +
    `${sheetNames.length === 1 ? '' : 's'}:</b> ${esc(sheetNames.join(', '))}. ` +
    'No patient names, no payment references — the figures above are the whole file.</p>' +
    '</div></section>'
  );
}

function render() {
  const d = doc();
  const root = d && d.getElementById('bill-root');
  if (!root) return;

  if (_error) {
    root.innerHTML =
      '<div class="rpt-error" role="alert"><p class="rpt-error-t">The expense ledger did not load.</p>' +
      '<p class="rpt-error-d">No totals are shown, because a partly-loaded ledger looks exactly like a complete one — ' +
      'and these figures go on an invoice. Check your connection and try again.</p>' +
      '<button type="button" class="btn pri" data-action="bill-reload">Try again</button></div>';
    return;
  }

  const parts = [filterBarHTML()];
  if (_busy && !_loaded) {
    parts.push('<div class="rpt-skel"></div><div class="rpt-skel"></div>');
    root.innerHTML = parts.join('');
    return;
  }

  const shown = filterByCategory(_rows, _range.category);
  parts.push(kpisHTML(expenseTotals(shown)));
  if (_stmt.open) parts.push(statementHTML(_rows));
  parts.push('<div class="rpt-sec-h"><h2>By category</h2><span class="rpt-ln"></span></div>');
  parts.push(byCategoryHTML(shown));
  parts.push(
    `<div class="rpt-sec-h"><h2>Transactions</h2><span class="bill-count">${esc(String(shown.length))} shown</span><span class="rpt-ln"></span></div>`
  );
  parts.push(listHTML(shown));
  root.innerHTML = parts.join('');
  applyBarWidths(root);
}

/**
 * Set the category bar widths from JS. CSP `style-src 'self'` blocks inline
 * style attributes, so a width written into the markup is dropped in production
 * and every bar renders as an empty track. Styles set via the CSSOM are exempt.
 * @param {any} root
 */
function applyBarWidths(root) {
  if (!root || typeof root.querySelectorAll !== 'function') return;
  for (const el of Array.from(root.querySelectorAll('.bill-cat-bar > i[data-w]'))) {
    const e = /** @type {any} */ (el);
    e.style.width = e.getAttribute('data-w') + '%';
  }
}

// ─── Add expense ───────────────────────────────────────────────────────────

function openAddExpense(presetCaseId) {
  const cases = caseOptions();
  const patientField = presetCaseId
    ? `<input type="hidden" id="exp-case" value="${esc(presetCaseId)}"/>` +
      `<div class="field"><label>Patient</label><input value="${esc((caseById(presetCaseId) || {}).patient_name || '')}" disabled/></div>`
    : '<div class="field"><label>Patient (optional)</label><select id="exp-case"><option value="">Programme-level expense</option>' +
      cases
        .map(
          (c) =>
            `<option value="${esc(c.case_id)}">${esc(c.patient_name || '')} — ${esc(c.reference_number || '')}</option>`
        )
        .join('') +
      '</select></div>';

  openModal(
    'Record expense',
    'Pass-through cost — receipts are the KRA audit trail',
    '<div class="fg">' +
      '<div class="field"><label>Category <span class="req">*</span></label><select id="exp-cat">' +
      PAYABLE_CATEGORIES.map((c) => `<option value="${esc(c.value)}">${esc(c.label)}</option>`).join('') +
      '</select></div>' +
      patientField +
      '<div class="field"><label>Amount (KES) <span class="req">*</span></label><input id="exp-amt" type="number" step="0.01" placeholder="0.00"/></div>' +
      `<div class="field"><label>Date <span class="req">*</span></label><input id="exp-date" type="date" value="${esc(isoDay(new Date()))}"/></div>` +
      '<div class="field"><label>Payment method</label><select id="exp-pay">' +
      PAYMENT_METHODS.map((m) => `<option value="${esc(m.value)}">${esc(m.label)}</option>`).join('') +
      '</select></div>' +
      '<div class="field"><label>Paid to</label><input id="exp-to" placeholder="Hospital, courier, SHA…"/></div>' +
      '<div class="field"><label>Payment reference</label><input id="exp-ref" placeholder="M-Pesa code, waybill no…"/></div>' +
      '<div class="field"><label>eTIMS invoice no.</label><input id="exp-etims" placeholder="From the vendor’s eTIMS invoice"/>' +
      '<div class="field-hint">Ask the vendor for an eTIMS-compliant invoice — our KRA PIN is P052329899E.</div></div>' +
      '<div class="field full"><label>Description</label><input id="exp-desc" placeholder="What was this payment for?"/></div>' +
      '<div class="field full"><label>Receipt / evidence (photo or PDF)</label>' +
      '<input type="file" id="exp-file" accept=".pdf,.jpg,.jpeg,.png,.heic" multiple/>' +
      '<div class="field-hint">Upload the receipt for the KRA audit trail. Multiple files allowed.</div></div>' +
      // is_billable has always existed on the row; v_20 hard-coded it true, so
      // every programme-level cost was invoiced whether or not it was recoverable.
      '<div class="field full"><label class="bill-chk"><input type="checkbox" id="exp-billable" checked/>' +
      '<span>Billable to the sponsor</span></label>' +
      '<div class="field-hint">Untick for costs the programme absorbs. Non-billable items stay in the ledger but leave the sponsor statement.</div></div>' +
      '<div class="field full"><label>Notes</label><textarea id="exp-notes" rows="2" placeholder="Additional context…"></textarea></div>' +
      '</div>',
    [
      { l: 'Cancel', v: 'ghost', k: 'cancel', fn: () => closeModal() },
      { l: 'Save expense', v: 'pri', k: 'save', fn: () => void execSaveExpense() },
    ]
  );
}

/** @param {string} id @returns {any} */
function fieldVal(id) {
  const d = doc();
  const el = d && d.getElementById(id);
  return el ? el.value : '';
}

async function execSaveExpense() {
  const d = doc();
  const input = {
    category: fieldVal('exp-cat'),
    amount: fieldVal('exp-amt'),
    expenseDate: fieldVal('exp-date'),
  };
  const bad = validateExpense(input);
  if (bad) {
    toast(bad, 'err');
    return;
  }
  setBtnBusy(true, 'Saving…', 'save');
  try {
    const caseId = fieldVal('exp-case') || null;
    const c = caseId ? caseById(caseId) : null;
    const files = (d && d.getElementById('exp-file') && d.getElementById('exp-file').files) || [];
    const up = await uploadReceipts(files);
    const billEl = d && d.getElementById('exp-billable');
    const row = buildExpenseRow(
      {
        caseId,
        programmeId: (c && c.programme_id) || null,
        patientName: (c && c.patient_name) || null,
        category: input.category,
        description: fieldVal('exp-desc'),
        amount: parseFloat(String(input.amount)) || 0,
        paymentMethod: fieldVal('exp-pay'),
        paidTo: fieldVal('exp-to'),
        paymentReference: fieldVal('exp-ref'),
        etimsInvoiceNo: fieldVal('exp-etims'),
        expenseDate: input.expenseDate,
        notes: fieldVal('exp-notes'),
        receiptPaths: up.paths,
        isBillable: billEl ? !!billEl.checked : true,
      },
      { userId: ((store.get(SLICES.SESSION) || {}).user || {}).id || null }
    );
    const res = await insertExpense(row);
    if (!res.ok) {
      // KEEP-OPEN-ON-FAIL: the typed form survives a failed save.
      setBtnBusy(false);
      toast(netErr(res.error, 'Could not save the expense'), 'err');
      return;
    }
    closeModal();
    toast(
      up.failed
        ? `Expense saved — but ${up.failed} receipt${up.failed === 1 ? '' : 's'} FAILED to upload. Re-open it and re-attach them.`
        : 'Expense saved',
      up.failed ? 'err' : 'ok'
    );
    void refresh();
  } catch (e) {
    setBtnBusy(false);
    toast(netErr(e, 'Could not save the expense'), 'err');
  }
}

// ─── Dispatch evidence ─────────────────────────────────────────────────────

function openDispatchEvidence() {
  const cases = caseOptions();
  openModal(
    'Dispatch evidence',
    'Photo of the package before dispatch',
    '<div class="fg">' +
      '<div class="field"><label>Patient <span class="req">*</span></label><select id="de-case"><option value="">Select patient…</option>' +
      cases
        .map(
          (c) =>
            `<option value="${esc(c.case_id)}">${esc(c.patient_name || '')} — ${esc(c.reference_number || '')}</option>`
        )
        .join('') +
      '</select></div>' +
      '<div class="field"><label>Product code</label><input id="de-code" placeholder="From the packaging label"/></div>' +
      '<div class="field"><label>Batch number</label><input id="de-batch" placeholder="Batch / lot number"/></div>' +
      '<div class="field"><label>Quantity (packs)</label><input id="de-qty" type="number" value="1"/></div>' +
      '<div class="field full"><label>Photo of package <span class="req">*</span></label>' +
      '<input type="file" id="de-file" accept=".jpg,.jpeg,.png,.heic" multiple capture="environment"/>' +
      '<div class="field-hint">Photograph the medication package showing the product label.</div></div>' +
      '<div class="field full"><label>Notes</label><textarea id="de-notes" rows="2" placeholder="Any observations…"></textarea></div>' +
      '</div>',
    [
      { l: 'Cancel', v: 'ghost', k: 'cancel', fn: () => closeModal() },
      { l: 'Save evidence', v: 'pri', k: 'save', fn: () => void execDispatchEvidence() },
    ]
  );
}

async function execDispatchEvidence() {
  const d = doc();
  const caseId = fieldVal('de-case');
  const files = (d && d.getElementById('de-file') && d.getElementById('de-file').files) || [];
  if (!caseId) {
    toast('Select a patient', 'err');
    return;
  }
  if (!files.length) {
    toast('Take a photo of the package', 'err');
    return;
  }
  setBtnBusy(true, 'Uploading…', 'save');
  try {
    const c = caseById(caseId);
    const up = await uploadReceipts(files, { prefix: 'dispatch/' + caseId + '_' });
    // THE PHOTO IS THE EVIDENCE (legacy invariant): never save a dispatch-evidence
    // row with no photo — it would assert a dispatch nobody can verify.
    if (!up.paths.length) {
      setBtnBusy(false);
      toast('Photo upload failed — nothing saved. Check your connection and try again.', 'err');
      return;
    }
    const row = buildExpenseRow(
      {
        caseId,
        programmeId: (c && c.programme_id) || null,
        patientName: (c && c.patient_name) || null,
        category: 'dispatch_evidence',
        description: 'Dispatch photo evidence',
        amount: 0,
        paymentMethod: 'not_applicable',
        expenseDate: isoDay(new Date()),
        notes: fieldVal('de-notes'),
        receiptPaths: up.paths,
        isBillable: false,
        productCode: fieldVal('de-code'),
        batchNumber: fieldVal('de-batch'),
        quantityDispatched: parseInt(String(fieldVal('de-qty')), 10) || 1,
      },
      { userId: ((store.get(SLICES.SESSION) || {}).user || {}).id || null }
    );
    const res = await insertExpense(row);
    if (!res.ok) {
      setBtnBusy(false);
      toast(netErr(res.error, 'Could not save the evidence'), 'err');
      return;
    }
    closeModal();
    toast(up.failed ? `Saved — but ${up.failed} photo(s) failed to upload.` : 'Dispatch evidence saved', up.failed ? 'err' : 'ok');
    void refresh();
  } catch (e) {
    setBtnBusy(false);
    toast(netErr(e, 'Could not save the evidence'), 'err');
  }
}

// ─── Detail + void ─────────────────────────────────────────────────────────

/** @param {string} id */
function openDetail(id) {
  const e = _rows.find((r) => r.expense_id === id);
  if (!e) return;
  const urls = parseEvidence(e);
  const evidence = urls.length
    ? urls
        .map(
          (u, i) =>
            `<button type="button" class="btn sm" data-action="bill-receipt" data-path="${esc(u)}">File ${i + 1}</button>`
        )
        .join(' ')
    : '<span class="bill-muted">No receipts attached</span>';
  const rowsHtml = [
    ['Date', e.expense_date || '—'],
    ['Amount', e.category === 'dispatch_evidence' ? '—' : money(amountOf(e.amount))],
    ['Category', categoryLabel(e.category)],
    ['Payment', methodLabel(e.payment_method)],
    ['Paid to', e.paid_to || '—'],
    ['Reference', e.payment_reference || '—'],
    ['eTIMS invoice', e.etims_invoice_no || '—'],
    ['Patient', e.patient_name || 'Programme-level'],
    ['Billable', e.is_billable ? 'Yes — invoiced to the sponsor' : 'No — absorbed by the programme'],
  ]
    .concat(e.product_code ? [['Product code', e.product_code]] : [])
    .concat(e.batch_number ? [['Batch', e.batch_number]] : [])
    .concat(e.quantity_dispatched ? [['Qty dispatched', String(e.quantity_dispatched)]] : [])
    .map(([k, v]) => `<div><dt>${esc(String(k))}</dt><dd>${esc(String(v))}</dd></div>`)
    .join('');

  openModal(
    'Expense detail',
    categoryLabel(e.category),
    `<dl class="bill-detail">${rowsHtml}</dl>` +
      `<div class="bill-detail-b"><h4>Description</h4><p>${esc(e.description || '—')}</p></div>` +
      (e.notes ? `<div class="bill-detail-b"><h4>Notes</h4><p>${esc(e.notes)}</p></div>` : '') +
      `<div class="bill-detail-b"><h4>Receipts &amp; evidence</h4><p>${evidence}</p></div>`,
    [{ l: 'Close', v: 'ghost', k: 'close', fn: () => closeModal() }]
  );
}

/** @param {string} id */
function confirmDelete(id) {
  const e = _rows.find((r) => r.expense_id === id);
  if (!e) return;
  openModal(
    'Void this expense?',
    categoryLabel(e.category) + ' · ' + (e.expense_date || ''),
    '<p>This removes <b>' +
      esc(money(amountOf(e.amount))) +
      '</b> from the ledger and from every total on this page.</p>' +
      '<p class="bill-muted">The row is <b>voided, not erased</b> — it stays in the database for the audit trail, ' +
      'and the void is recorded against your name.</p>',
    [
      { l: 'Cancel', v: 'ghost', k: 'cancel', fn: () => closeModal() },
      {
        l: 'Void expense',
        v: 'danger',
        k: 'del',
        fn: () => void execDelete(id, e),
      },
    ]
  );
}

/** @param {string} id @param {any} e */
async function execDelete(id, e) {
  setBtnBusy(true, 'Voiding…', 'del');
  const res = await softDeleteExpense(id, { amount: e.amount, caseId: e.case_id, category: e.category });
  if (!res.ok) {
    setBtnBusy(false);
    toast(netErr(res.error, 'Could not void the expense'), 'err');
    return;
  }
  closeModal();
  toast('Expense voided', 'ok');
  void refresh();
}

/** @param {string} path */
async function openReceipt(path) {
  const res = await signedReceiptUrl(path);
  if (!res.ok || !res.data) {
    toast(netErr(res.error, 'Could not open the receipt'), 'err');
    return;
  }
  const w = /** @type {any} */ (globalThis).window;
  if (w && typeof w.open === 'function') w.open(res.data, '_blank', 'noopener');
}

// ─── Statement export ──────────────────────────────────────────────────────

/** @param {boolean} [internal] */
function onExportStatement(internal) {
  const prog = store.get(SLICES.PROGRAMMES) || {};
  const byCode = prog.byCode || {};
  const list = Object.keys(byCode).map((k) => byCode[k]);
  const scoped =
    _stmt.programme === 'all' ? _rows : _rows.filter((e) => String(e.programme_id) === String(_stmt.programme));
  const progLabel =
    _stmt.programme === 'all'
      ? 'All programmes'
      : (list.find((p) => String(p.programme_id) === String(_stmt.programme)) || {}).display_name || 'Programme';
  const stmt = buildStatement(scoped, {
    programmeLabel: progLabel,
    from: _range.from,
    to: _range.to,
    generatedAt: new Date().toISOString().slice(0, 16).replace('T', ' '),
    billableOnly: _stmt.billableOnly,
  });
  if (!stmt.lines.length) {
    toast('Nothing to export — no items match this scope', 'err');
    return;
  }
  // The role gate is re-checked HERE, not only when the button was drawn: hiding
  // a control is a UI courtesy, refusing the action is the actual restriction.
  const wantInternal = !!internal;
  if (wantInternal && !isAdmin((store.get(SLICES.SESSION) || {}).role)) {
    toast('The internal copy is restricted to administrators', 'err');
    return;
  }
  const res = writeStatementWorkbook(
    stmt,
    scoped,
    wantInternal ? 'INTERNAL_' + statementFileName(stmt) : undefined,
    { internal: wantInternal }
  );
  if (!res.ok) {
    toast(
      res.code === 'NO_XLSX'
        ? 'Exports unavailable — the spreadsheet script did not load. Reload when online.'
        : netErr(res.error, 'Export failed'),
      'err'
    );
    return;
  }
  toast('Statement exported', 'ok');
}

// ─── Wiring ────────────────────────────────────────────────────────────────

function wireOnce() {
  if (_wired) return;
  _wired = true;

  register('bill-add', () => openAddExpense());
  register('bill-dispatch', () => openDispatchEvidence());
  register('bill-reload', () => {
    _error = null;
    void refresh();
  });
  register('bill-preset', (el) => {
    const which = el.getAttribute('data-preset') === 'last' ? 'last' : 'this';
    const p = monthPreset(which);
    _range.from = p.from;
    _range.to = p.to;
    void refresh();
  });
  register('bill-detail', (el) => {
    const id = el.getAttribute('data-id');
    if (id) openDetail(id);
  });
  register('bill-delete', (el) => {
    const id = el.getAttribute('data-id');
    if (id) confirmDelete(id);
  });
  register('bill-receipt', (el) => {
    const p = el.getAttribute('data-path');
    if (p) void openReceipt(p);
  });
  register('bill-stmt', () => {
    _stmt.open = !_stmt.open;
    render();
  });
  register('bill-stmt-xlsx', () => onExportStatement(false));
  register('bill-stmt-internal', () => onExportStatement(true));
  register('bill-print', () => {
    const w = /** @type {any} */ (globalThis).window;
    if (w && typeof w.print === 'function') w.print();
  });

  const d = doc();
  if (d) {
    d.addEventListener('change', (/** @type {any} */ ev) => {
      const t = ev.target;
      if (!t || !t.id) return;
      if (t.id === 'bill-from' || t.id === 'bill-to') {
        // An inverted range returns nothing and looks like "no spend this month" —
        // refuse it rather than render a confident empty ledger.
        const from = t.id === 'bill-from' ? t.value : _range.from;
        const to = t.id === 'bill-to' ? t.value : _range.to;
        if (from && to && from > to) {
          toast('The From date is after the To date', 'err');
          return;
        }
        _range.from = from;
        _range.to = to;
        void refresh();
        return;
      }
      if (t.id === 'bill-cat') {
        _range.category = t.value;
        render();
        return;
      }
      if (t.id === 'bill-stmt-prog') {
        _stmt.programme = t.value;
        render();
        return;
      }
      if (t.id === 'bill-stmt-billable') {
        _stmt.billableOnly = !!t.checked;
        render();
      }
    });
  }

  // A programme-pill change rescopes the statement preview, not the ledger: the
  // ledger is a period query, and the pill is a reporting scope.
  store.subscribe(SLICES.PROGRAMMES, () => {
    if (mounted()) render();
  });
}

// ─── Registration ──────────────────────────────────────────────────────────

/** Test-only: reset view state between runs. @returns {void} */
export function _resetBillingView() {
  _range = { from: '', to: '', category: 'all' };
  _rows = [];
  _loaded = false;
  _error = null;
  _busy = false;
  _stmt = { open: false, programme: 'all', billableOnly: true };
}

/** The loaded rows (tests / headless drivers). @returns {any[]} */
export function billingRows() {
  return _rows.slice();
}

/** Open the statement panel programmatically (headless screenshot driver). @returns {void} */
export function openStatementPanel() {
  _stmt.open = true;
  render();
}

/** Register the Billing view. Call once at boot from main.js (and the harness). @returns {void} */
export function registerBilling() {
  registerView('billing', {
    render(host) {
      host.innerHTML = skeleton();
      wireOnce();
      render();
    },
    load() {
      return refresh();
    },
  });
}
